/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import {
    ConfigurationParameters,
    Configuration,
    VodOttActionDetailsApi,
    OfferVodOttActionDetailsGetRequest,
} from 'client-code/action-details';
import { Constants } from '../constants';
import { Log } from '../logging/Log';
import { UniqueIdentifier } from '../utils/UniqueIdentifier';
import { CommonHeaders } from '../CommonHeaders';
import { DeviceModel } from '../device/DeviceModel';
import { IActionDetailsServiceRepository } from '../interfaces/IActionDetailsServiceRepository';
import {
    TActionDetailsList,
    TActionDetailsParams,
    TServicesParams,
    TVodOttActionDetailsList,
} from '../types';

export class ActionDetailsServiceRepositoryImpl implements IActionDetailsServiceRepository {
    mParams: TServicesParams;

    TAG: string = 'ActionDetailsServiceRepositoryImpl';

    constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private async getOfferActionDetailsFromService(
        vodOttActionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> {
        try {
            const { offerId, isCatchUp, isStartOver, screenType, actionDetailsNetworkCallId } =
                vodOttActionDetailsParams;
            const commonHeaders = new CommonHeaders(screenType).headers;

            const configurationParameters: ConfigurationParameters = {
                apiKey: this.mParams.accessToken,
                basePath: this.mParams.basePath,
                baseOptions: {
                    timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                    headers: commonHeaders,
                },
            };

            const requestParameters: OfferVodOttActionDetailsGetRequest = {
                offerId,
                isCatchUp,
                isStartOver,
                timeZoneName: DeviceModel.getTVProperties().getDefaultTimeZone(),
                applicationName: DeviceModel.getTiVoSettings().getAppName(),
                applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
                applicationVersion: commonHeaders.ApplicationVersion,
                bodyId: this.mParams.bodyId,
                deviceType: DeviceModel.getTiVoSettings().getDeviceType(),
                productName: DeviceModel.getTiVoSettings().getAppName(),
                acceptLanguage: DeviceModel.getTVProperties().getDateLocale(),
                xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            };

            const configuration = new Configuration(configurationParameters);
            const actionsApi = new VodOttActionDetailsApi(configuration);
            const actionsApiResponseObj = await actionsApi.offerVodOttActionDetailsGet(
                requestParameters,
            );
            const actionDetailsApiResponse: TActionDetailsList = {
                actionDetailsNetworkCallId,
                actionDetails: actionsApiResponseObj.data as TVodOttActionDetailsList,
            };
            return actionDetailsApiResponse;
        } catch (error: any) {
            error.actionDetailsNetworkCallId = vodOttActionDetailsParams.actionDetailsNetworkCallId;
            Log.e(this.TAG, 'getOfferActionDetailsFromService()', error);
            throw error;
        }
    }

    async fetchOfferActionDetails(
        actionDetailsParams: TActionDetailsParams,
    ): Promise<TActionDetailsList> {
        try {
            return await this.getOfferActionDetailsFromService(actionDetailsParams);
        } catch (error) {
            Log.e(this.TAG, 'error', error);
            throw error;
        }
    }
}
